import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageBanner } from "@/components/ui/PageBanner";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { IMG } from "@/lib/assets";
import { Plus, Wand2, Loader2, ChevronLeft, ChevronRight, Check, Trash2, CalendarClock, Flame, TrendingUp, BatteryLow, RotateCcw, Sparkles } from "lucide-react";

const KINDS = {
  strength: { label: "Strength", c: "#CCFF00" },
  hiit: { label: "HIIT", c: "#FF6B6B" },
  conditioning: { label: "Conditioning", c: "#FFB340" },
  run: { label: "Run", c: "#3DD6FF" },
  yoga: { label: "Yoga", c: "#B57BFF" },
  taichi: { label: "Mobility", c: "#7C9CFF" },
  mobility: { label: "Mobility", c: "#5CE1A6" },
  class: { label: "Class", c: "#3D7BFF" },
  other: { label: "Other", c: "#8A91A6" },
};
const PHASE = {
  start: { title: "Let's get started", Icon: Sparkles, c: "#CCFF00" },
  build: { title: "Build phase", Icon: TrendingUp, c: "#CCFF00" },
  deload: { title: "Easy week", Icon: BatteryLow, c: "#3DD6FF" },
  restart: { title: "Ease back in", Icon: RotateCcw, c: "#FFB340" },
  spike: { title: "Big jump in load", Icon: Flame, c: "#FF6B6B" },
};

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfWeek = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - wd); return x; };

export default function Diary() {
  const qc = useQueryClient();
  const [weekOffset, setWeekOffset] = useState(0);
  const [editing, setEditing] = useState(null);
  const [planning, setPlanning] = useState(false);
  const { data } = useQuery({ queryKey: ["diary"], queryFn: async () => (await api.get("/api/diary", { params: { days_back: 120, days_ahead: 60 } })).data });
  const { data: cycle } = useQuery({ queryKey: ["diary-cycle"], queryFn: async () => (await api.get("/api/diary/cycle")).data });
  const items = useMemo(() => data?.items || [], [data]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["diary"] }); qc.invalidateQueries({ queryKey: ["diary-cycle"] }); };

  const weekStart = useMemo(() => { const s = startOfWeek(new Date()); s.setDate(s.getDate() + weekOffset * 7); return s; }, [weekOffset]);
  const days = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const byDay = useMemo(() => {
    const m = {};
    items.forEach((it) => { const k = dayKey(new Date(it.date)); (m[k] = m[k] || []).push(it); });
    return m;
  }, [items]);
  const todayKey = dayKey(new Date());

  const planWeek = async () => {
    setPlanning(true);
    try {
      const { data: d } = await api.post("/api/diary/plan-week", null, { params: { tz_offset: new Date().getTimezoneOffset() } });
      toast.success(`Your coach planned ${d.created.length} sessions for the next 7 days`);
      setWeekOffset(0); refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setPlanning(false); }
  };

  const markDone = async (it) => {
    try { await api.patch(`/api/diary/${it.id}`, { status: "done" }); toast.success("Nice work. Session logged."); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const phase = PHASE[cycle?.phase] || PHASE.start;
  const maxLoad = Math.max(1, ...(cycle?.weeks || []).map((w) => w.load));

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} height="h-56 md:h-64" overline="your training cycle" title="Training Diary"
        subtitle="Log every session with its date and time, plan the week ahead, and let your OCTON coach steer your training cycle."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEditing({ status: "done" })} className="bg-white text-black hover:bg-acid rounded-full h-11 px-5" data-testid="diary-add">
              <Plus className="w-4 h-4" /> Log a session
            </Button>
            <Button onClick={planWeek} disabled={planning} className="bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-5 shadow-[0_0_30px_rgba(204,255,0,0.35)]" data-testid="diary-plan">
              {planning ? <><Loader2 className="w-4 h-4 animate-spin" /> Planning…</> : <><Wand2 className="w-4 h-4" /> Plan my week</>}
            </Button>
          </div>} />

      {/* Cycle + load */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-5 relative overflow-hidden rounded-xl border border-[#1E222A] bg-gradient-to-br from-[#10131A] to-[#0B0D11] p-6" data-testid="diary-cycle">
          <div className="absolute -top-20 -left-16 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: phase.c }} />
          <div className="relative flex items-start gap-4">
            <CoachAvatar size={56} />
            <div className="flex-1">
              <span className="mono-label" style={{ color: phase.c }}>coach says</span>
              <h2 className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
                <phase.Icon className="w-5 h-5" style={{ color: phase.c }} /> {phase.title}
                {cycle?.build_week && <span className="text-sm font-medium text-[#8A91A6]">· week {cycle.build_week} of 3</span>}
              </h2>
              <p className="text-[#B7BECB] text-sm mt-2">{cycle?.advice || "Loading your cycle…"}</p>
            </div>
          </div>
          {cycle?.phase === "build" && (
            <div className="relative flex gap-1.5 mt-5">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="flex-1">
                  <div className="h-2 rounded-full" style={{ background: n <= (cycle.build_week || 0) ? "#CCFF00" : n === 4 ? "#3DD6FF33" : "#1E222A" }} />
                  <p className="mono-label mt-1 text-center">{n === 4 ? "easy" : `build ${n}`}</p>
                </div>
              ))}
            </div>
          )}
          <div className="relative grid grid-cols-3 gap-3 mt-5">
            <Mini label="this week" value={cycle?.this_week_sessions ?? 0} sub="sessions" />
            <Mini label="last session" value={cycle?.days_since_last == null ? "–" : cycle.days_since_last === 0 ? "today" : `${cycle.days_since_last}d`} sub={cycle?.days_since_last ? "ago" : ""} />
            <Mini label="next up" value={cycle?.next_planned ? new Date(cycle.next_planned.date).toLocaleDateString(undefined, { weekday: "short" }) : "–"}
              sub={cycle?.next_planned ? new Date(cycle.next_planned.date).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "plan a session"} />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="lg:col-span-7 rounded-xl border border-[#1E222A] bg-[#0F1115] p-6" data-testid="diary-load">
          <div className="flex items-end justify-between mb-4">
            <div>
              <span className="mono-label text-acid">training load</span>
              <h3 className="font-heading font-bold text-xl tracking-tight">Last 6 weeks</h3>
            </div>
            <p className="text-xs text-[#8A91A6] max-w-[14rem] text-right">Load = minutes × effort. Build gradually, then take an easy week.</p>
          </div>
          <div className="flex items-end gap-3 h-40">
            {(cycle?.weeks || []).map((w, i, arr) => {
              const h = Math.max(4, (w.load / maxLoad) * 100);
              const isNow = i === arr.length - 1;
              return (
                <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end h-full">
                  <span className="mono-label mb-1">{w.sessions}×</span>
                  <motion.div initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ duration: 0.8, delay: i * 0.06 }}
                    className="w-full rounded-t-md" title={`${w.minutes} min · load ${w.load}`}
                    style={{ background: isNow ? "linear-gradient(180deg,#CCFF00,#7A9900)" : "linear-gradient(180deg,#3D7BFF,#1B3A80)",
                             boxShadow: isNow ? "0 0 20px rgba(204,255,0,0.35)" : "none" }} />
                  <span className="mono-label mt-2">{isNow ? "now" : new Date(w.week_start).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Week calendar */}
      <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-4 md:p-6" data-testid="diary-week">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekOffset((w) => w - 1)} className="w-9 h-9 rounded-full border border-[#343A4A] flex items-center justify-center hover:border-acid" aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-center">
            <p className="font-heading font-bold">{days[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} – {days[6].toLocaleDateString(undefined, { day: "numeric", month: "short" })}</p>
            {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="mono-label text-acid">back to this week</button>}
          </div>
          <button onClick={() => setWeekOffset((w) => w + 1)} className="w-9 h-9 rounded-full border border-[#343A4A] flex items-center justify-center hover:border-acid" aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {days.map((d) => {
            const k = dayKey(d);
            const list = (byDay[k] || []).sort((a, b) => a.date.localeCompare(b.date));
            const isToday = k === todayKey;
            return (
              <div key={k} className={`rounded-lg p-2 min-h-[7rem] border ${isToday ? "border-acid/60 bg-acid/5" : "border-[#1E222A] bg-[#0B0D11]"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`mono-label ${isToday ? "text-acid" : ""}`}>{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                  <button onClick={() => { const at = new Date(d); at.setHours(18, 0, 0, 0); setEditing({ status: at > new Date() ? "planned" : "done", date: at }); }}
                    className="font-heading font-black text-lg hover:text-acid" title="Add a session on this day">{d.getDate()}</button>
                </div>
                <div className="space-y-1.5">
                  {list.map((it) => {
                    const k2 = KINDS[it.kind] || KINDS.other;
                    const planned = it.status === "planned";
                    return (
                      <button key={it.id} onClick={() => it.source === "diary" && setEditing(it)}
                        className={`w-full text-left rounded-md px-2 py-1.5 text-xs border transition-colors ${planned ? "border-dashed" : ""}`}
                        style={{ borderColor: `${k2.c}66`, background: planned ? "transparent" : `${k2.c}14` }} data-testid="diary-item">
                        <span className="flex items-center gap-1 font-medium text-white/90 truncate">
                          {!planned && <Check className="w-3 h-3 shrink-0" style={{ color: k2.c }} />}{it.title}
                        </span>
                        <span className="text-[#8A91A6]">{new Date(it.date).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {it.duration_min}m</span>
                        {planned && it.source === "diary" && new Date(it.date) < new Date(Date.now() + 864e5) && (
                          <span onClick={(e) => { e.stopPropagation(); markDone(it); }} className="mt-1 block text-acid hover:underline">Mark done</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          {Object.entries(KINDS).map(([k, v]) => (
            <span key={k} className="mono-label flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.c }} />{v.label}</span>
          ))}
          <span className="mono-label flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm border border-dashed border-[#8A91A6]" />planned</span>
        </div>
      </div>

      <EntryDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
    </div>
  );
}

function Mini({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-3">
      <p className="mono-label">{label}</p>
      <p className="font-heading font-black text-xl mt-1">{value}</p>
      <p className="text-[11px] text-[#8A91A6]">{sub}</p>
    </div>
  );
}

function toLocalInput(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

function EntryDialog({ entry, onClose, onSaved }) {
  const isEdit = !!entry?.id;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm(entry ? {
      date: toLocalInput(entry.date || new Date()), kind: entry.kind || "strength", title: entry.title || "",
      duration_min: entry.duration_min || 60, rpe: entry.rpe || 6, notes: entry.notes || "", status: entry.status || "done",
    } : null);
  }, [entry]);
  if (!entry || !form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = { ...form, date: new Date(form.date).toISOString(), duration_min: Number(form.duration_min), rpe: Number(form.rpe) };
    try {
      if (isEdit) await api.patch(`/api/diary/${entry.id}`, body); else await api.post("/api/diary", body);
      toast.success(body.status === "planned" ? "Added to your plan" : "Session logged");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/api/diary/${entry.id}`); toast("Removed"); onSaved(); } catch (e) { toast.error(apiErr(e)); }
  };
  const effort = ["", "Very easy", "Easy", "Easy", "Moderate", "Moderate", "Hard", "Hard", "Very hard", "Max effort", "Max effort"][form.rpe];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="surface border-[#1E222A] max-w-lg">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarClock className="w-5 h-5 text-acid" />{isEdit ? "Edit session" : "Log or plan a session"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="inline-flex rounded-full border border-[#343A4A] p-1">
            {[["done", "I did this"], ["planned", "I plan to do this"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, status: v })}
                className={`px-4 h-8 rounded-full text-sm ${form.status === v ? "bg-acid text-black" : "text-[#8A91A6]"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mono-label">Date and time</Label>
              <Input type="datetime-local" value={form.date} onChange={set("date")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="diary-date" /></div>
            <div><Label className="mono-label">Minutes</Label>
              <Input type="number" min="5" max="600" value={form.duration_min} onChange={set("duration_min")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          </div>
          <div>
            <Label className="mono-label">Type</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {Object.entries(KINDS).filter(([k]) => k !== "class").map(([k, v]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                  className="px-3 h-8 rounded-full text-sm border transition-colors"
                  style={form.kind === k ? { background: v.c, color: "#050505", borderColor: v.c } : { borderColor: "#343A4A", color: "#B7BECB" }}>{v.label}</button>
              ))}
            </div>
          </div>
          <div><Label className="mono-label">Title (optional)</Label>
            <Input value={form.title} onChange={set("title")} placeholder="e.g. Leg day, 5k run" className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
          <div>
            <Label className="mono-label">Effort: {form.rpe}/10 · {effort}</Label>
            <input type="range" min="1" max="10" value={form.rpe} onChange={set("rpe")} className="w-full mt-2 accent-[#CCFF00]" />
          </div>
          <div><Label className="mono-label">Notes</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="How did it go?" className="mt-1.5 bg-[#0F1115] border-[#1E222A]" /></div>
        </div>
        <DialogFooter className="gap-2">
          {isEdit && <Button variant="outline" onClick={remove} className="border-[#343A4A] bg-transparent hover:bg-white/5 mr-auto"><Trash2 className="w-4 h-4" /> Remove</Button>}
          <Button onClick={save} disabled={busy} className="bg-acid text-black hover:bg-[#AACC00]" data-testid="diary-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
